import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY;

let _app;
const getFirebaseApp = () => {
  if (!_app) _app = initializeApp(firebaseConfig);
  return _app;
};

export const pushConfigured = () => Boolean(VAPID_KEY && firebaseConfig.apiKey);

/** Request permission + register the SW + return an FCM device token. Throws on failure. */
export async function requestPushToken() {
  if (!pushConfigured()) throw new Error('Push isn\u2019t configured yet (missing VAPID key).');
  if (!(await isSupported())) throw new Error('Push notifications aren\u2019t supported in this browser.');
  if (!('serviceWorker' in navigator)) throw new Error('Service workers unavailable.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was blocked.');

  // Register the FCM SW under an isolated scope so it doesn't collide with
  // the PWA `/service-worker.js` (which owns scope `/`). Two different SW
  // scripts can't co-exist at the same scope — one would silently replace
  // the other, breaking either push delivery or offline shell. FCM only
  // needs to receive `push` events; it doesn't need any scope reach.
  const registration = await navigator.serviceWorker.register(
    '/firebase-messaging-sw.js',
    { scope: '/firebase-cloud-messaging-push-scope' },
  );
  const messaging = getMessaging(getFirebaseApp());
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  if (!token) throw new Error('Could not obtain a push token.');
  return token;
}

/** Subscribe to foreground messages. Returns an unsubscribe fn (or noop). */
export async function onForegroundMessage(cb) {
  if (!pushConfigured() || !(await isSupported())) return () => {};
  const messaging = getMessaging(getFirebaseApp());
  return onMessage(messaging, cb);
}
